import {
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    withStyles,
    WithStyles
} from "@material-ui/core";
import React from "react";
import Typography from "@material-ui/core/Typography/Typography";
import { FormTextField } from "../../components/FormComponents/FormTextField";
import { FormNativeSelect } from "../../components/FormComponents/FormNativeSelect";
import { useTranslation } from "react-i18next";
import { styles } from "./RegisterForm.styles";
import { emailPattern, phonePattern } from "../../../utilities/generalUtils";
import { AcceptTermsConditionsComponent } from "../AcceptTermsConditionsComponent";

export type RegisterFormVariant = "register" | "addNewUserAdmin" | "saveUserDetails";

export interface IRegisterFormProps extends WithStyles<typeof styles> {
    details: any;
    registerError?: any;
    handleChange: any;
    handleCountry: any;
    allCountries: any;
    type?: RegisterFormVariant;
    handleHowDidYouHearAboutUs?: any;
    anotherDeliveryAddress: any;
    setAnotherDeliveryAddress: any;
    agrees: any;
    handleAgree?: any;
}

const RegisterFormPure: React.FunctionComponent<IRegisterFormProps> = ({
    details,
    registerError,
    handleChange,
    handleCountry,
    handleHowDidYouHearAboutUs,
    allCountries,
    type,
    classes,
    anotherDeliveryAddress,
    setAnotherDeliveryAddress,
    agrees,
    handleAgree
}) => {
    const { t } = useTranslation();

    const itemsForHowDidYouHearAboutUs = [
        { name: "searchEngine", label: t("register.searchEngine") },
        { name: "dbWebsite", label: t("register.dbWebsite") },
        { name: "recommendation", label: t("register.recommendation") }
    ];

    const handleAnotherDeliveryAddress = () => {
        setAnotherDeliveryAddress(!anotherDeliveryAddress);
    };

    return (
        <>
            <Grid item xs={12}>
                <Typography variant="h3">{t("register.signIn")}</Typography>
            </Grid>
            {(type === "addNewUserAdmin" || type === "register") && (
                <Grid item xs={6}>
                    <FormTextField
                        id="email"
                        required
                        onChange={handleChange}
                        value={details.email}
                        label={t("general.email")}
                        pattern={emailPattern}
                    />
                </Grid>
            )}

            <Grid item xs={12}>
                <Typography variant="h3">{t("register.companyInfo")}</Typography>
            </Grid>
            <Grid item xs={6}>
                <FormTextField
                    id="company"
                    required
                    onChange={handleChange}
                    value={details.company}
                    label={t("general.company")}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField id="tin" onChange={handleChange} value={details.tin} label={t("register.taxId")} />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h3">{t("register.userInfo")}</Typography>
            </Grid>

            <Grid item xs={4}>
                <FormTextField
                    id="firstName"
                    required
                    onChange={handleChange}
                    value={details.firstName}
                    label={t("general.firstName")}
                />
            </Grid>

            <Grid item xs={4}>
                <FormTextField
                    id="lastName"
                    required
                    onChange={handleChange}
                    value={details.lastName}
                    label={t("general.lastName")}
                />
            </Grid>

            <Grid item xs={4}>
                <FormTextField
                    id="jobPosition"
                    onChange={handleChange}
                    value={details.jobPosition}
                    label={t("register.jobPosition")}
                />
            </Grid>

            <Grid item xs={12}>
                {anotherDeliveryAddress ? (
                    <Typography variant="h4">Billing Address</Typography>
                ) : (
                    <Typography variant="h3">{t("register.addressInfo")}</Typography>
                )}
            </Grid>

            <Grid item xs={6}>
                <FormTextField
                    id="addressLine1"
                    required
                    onChange={handleChange}
                    value={details.addressLine1}
                    label={t("register.addressLine1")}
                />
            </Grid>

            <Grid item xs={6}>
                <FormTextField
                    id="addressLine2"
                    onChange={handleChange}
                    value={details.addressLine2}
                    label={t("register.addressLine2")}
                />
            </Grid>

            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <FormTextField
                            id="postcode"
                            required
                            onChange={handleChange}
                            value={details.postcode}
                            label={t("general.postcode")}
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <FormTextField
                            id="city"
                            required
                            onChange={handleChange}
                            value={details.city}
                            label={t("general.city")}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormNativeSelect
                            label={t("general.country")}
                            options={allCountries}
                            onChange={handleCountry}
                            name="country"
                            value={details.country}
                            required
                            emptyCaption={"Select country"}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <FormControlLabel
                    control={<Checkbox onChange={handleAnotherDeliveryAddress} checked={!anotherDeliveryAddress} />}
                    label="Use same information for the delivery address"
                />
            </Grid>
            {anotherDeliveryAddress ? (
                <>
                    <Grid item xs={12}>
                        <Typography variant="h4">Delivery Address</Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <FormTextField
                            id="deliveryAddressLine1"
                            onChange={handleChange}
                            value={details.deliveryAddressLine1}
                            label={t("register.addressLine1")}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormTextField
                            id="deliveryAddressLine2"
                            onChange={handleChange}
                            value={details.deliveryAddressLine2}
                            label={t("register.addressLine2")}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <FormTextField
                                    id="deliveryPostcode"
                                    onChange={handleChange}
                                    value={details.deliveryPostcode}
                                    label={t("general.postcode")}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <FormTextField
                                    id="deliveryCity"
                                    onChange={handleChange}
                                    value={details.deliveryCity}
                                    label={t("general.city")}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormNativeSelect
                                    label={t("general.country")}
                                    options={allCountries}
                                    onChange={handleCountry}
                                    name="deliveryCountry"
                                    value={details.deliveryCountry}
                                    emptyCaption={"Select country"}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            ) : null}

            <Grid item xs={12}>
                <Typography variant="h3">{t("register.contactInfo")}</Typography>
            </Grid>

            <Grid item xs={4}>
                <FormTextField
                    id="phone"
                    value={details.phone}
                    label={t("general.phone")}
                    pattern={phonePattern}
                    onChange={handleChange}
                />
            </Grid>

            <Grid item xs={4}>
                <FormTextField
                    id="mobilePhone"
                    value={details.mobilePhone}
                    label={t("general.mobilePhone")}
                    pattern={phonePattern}
                    onChange={handleChange}
                />
            </Grid>

            <Grid item xs={4} className={classes.website}>
                <FormTextField id="website" value={details.website} label={"website"} onChange={handleChange} />
            </Grid>

            <>
                {type === "register" && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h3">{t("register.howDidYouHear")}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl component="fieldset" fullWidth>
                                <RadioGroup
                                    value={details.howDidYouHearAboutDS}
                                    onChange={event => handleHowDidYouHearAboutUs(event.target.value, "")}
                                >
                                    {itemsForHowDidYouHearAboutUs.map(v => (
                                        <FormControlLabel
                                            value={v.name}
                                            control={<Radio />}
                                            label={v.label}
                                            key={v.name}
                                            name={v.name}
                                        />
                                    ))}
                                    <FormControlLabel
                                        value="other"
                                        control={<Radio />}
                                        label={
                                            <FormTextField
                                                disabled={details.howDidYouHearAboutDS !== "other"}
                                                label={t("register.other")}
                                                value={details.howDidYouHearAboutDSOther}
                                                onChange={event =>
                                                    handleHowDidYouHearAboutUs("other", event.target.value)
                                                }
                                            />
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </>
                )}
                <>
                    <AcceptTermsConditionsComponent agrees={agrees} handleAgree={handleAgree} />
                    <Typography variant="body2">{t("basket.requiredFields")}</Typography>
                </>
            </>

            {registerError && (
                <Grid item xs={12}>
                    <Typography variant="h5" className={classes.errorText}>
                        {registerError}
                    </Typography>
                </Grid>
            )}
        </>
    );
};

export const RegisterForm = withStyles(styles)(RegisterFormPure);
